/* eslint-disable array-bracket-newline */
import { Chain, Connector, ConnectorData } from 'wagmi';
import TonWeb from 'tonweb';
import {
  TonConnect,
  WalletInfoCurrentlyEmbedded,
  WalletInfoCurrentlyInjected,
  WalletInfoRemote,
  isWalletInfoCurrentlyEmbedded,
  isWalletInfoCurrentlyInjected,
  isWalletInfoRemote,
  toUserFriendlyAddress,
} from '@tonconnect/sdk';
import { Router, ROUTER_REVISION, ROUTER_REVISION_ADDRESS } from '@ston-fi/sdk';
import { TON_CHAIN_ID } from '../helpers/customChains';
import {
  WalletsName,
  WalletsType,
} from '../components/ConnectedWallet/wallets.ls';
import { AuthAPI } from '../store/createStore';
import {
  ISubWallet,
  ISubWalletConnectorOptions,
} from '../components/Modal/Connect';

const LS_TON_CURRENT_WALLET_NAME_KEY = 'kattana-simple.ton_current_wallet_name';

export const tonWebProvider = new TonWeb.HttpProvider(undefined, {
  apiKey: 'bd7224e325f771e7ef0105e61958e7492c35eb9ee12b3bac4908b9369ffd46c6',
});

export const tonRouter = new Router(tonWebProvider, {
  revision: ROUTER_REVISION.V1,
  address: ROUTER_REVISION_ADDRESS.V1,
});

const getSubWallets = async (): Promise<ISubWallet[]> => {
  const walletInfoList = await TonConnect.getWallets();

  const remoteWallets = walletInfoList.filter(isWalletInfoRemote);
  const embeddedWallets = walletInfoList.filter(isWalletInfoCurrentlyEmbedded);
  const injectedWallets = walletInfoList.filter(isWalletInfoCurrentlyInjected);

  // Parse and filter duplicates in wallets
  const wallets = [...remoteWallets, ...embeddedWallets, ...injectedWallets]
    .map((wallet) => ({
      id: wallet.appName,
      image: wallet.imageUrl,
      title: wallet.name,
      isLink: isWalletInfoRemote(wallet),
    }))
    .reduce<Map<string, ISubWallet>>(
      (cons, item) => cons.set(item.id, item),
      new Map()
    )
    .values();

  return Array.from(wallets);
};

const setCurrentWallet = (id: string) => {
  if (window.localStorage) {
    window.localStorage.setItem(LS_TON_CURRENT_WALLET_NAME_KEY, id);
  }
};

type AcceptableWallet =
  | WalletInfoCurrentlyInjected
  | WalletInfoCurrentlyEmbedded
  | WalletInfoRemote;

const checkCurrentWallet = (wallet: any): boolean => {
  return (
    wallet &&
    (isWalletInfoCurrentlyEmbedded(wallet) ||
      isWalletInfoCurrentlyInjected(wallet) ||
      isWalletInfoRemote(wallet))
  );
};

export class TonConnector extends Connector<
  TonConnect,
  ISubWalletConnectorOptions
> {
  readonly id = WalletsType.TON;
  readonly name = WalletsName.TON;
  readonly ready = true;
  // readonly options =
  // readonly chains =

  provider: TonConnect;
  signer: any;
  unsubscribe: any;

  constructor(chains: Chain[]) {
    super({ chains, options: { getSubWallets, setCurrentWallet } });

    this.provider = new TonConnect();
  }

  async getProvider(config?: { chainId?: number }) {
    return this.provider;
  }

  async connect(config?: {
    chainId?: number;
  }): Promise<Required<ConnectorData>> {
    this.emit('message', { type: 'connecting' });

    if (!this.provider.wallet) {
      try {
        const walletInfoList = await this.provider.getWallets();

        let wallet: AcceptableWallet;

        const currentWalletAppName = window.localStorage?.getItem(
          LS_TON_CURRENT_WALLET_NAME_KEY
        );
        const currentWallet = walletInfoList.find(
          (w) => w.appName === currentWalletAppName
        );
        if (checkCurrentWallet(currentWallet)) {
          wallet = currentWallet as AcceptableWallet;
        } else {
          const injectedWallet = walletInfoList.find(
            isWalletInfoCurrentlyInjected
          );
          const embeddedWallet = walletInfoList.find(
            isWalletInfoCurrentlyEmbedded
          );
          const remoteWallet = walletInfoList.find(isWalletInfoRemote);
          if (embeddedWallet || injectedWallet || remoteWallet) {
            wallet = (embeddedWallet || injectedWallet || remoteWallet)!;
          } else {
            throw new Error('Could not find any ton wallets');
          }
        }

        const { ok, token } = await AuthAPI.generate();

        if (!ok || !token) {
          throw new Error('Ton Proof payload not generated');
        }

        if (
          isWalletInfoCurrentlyInjected(wallet) ||
          isWalletInfoCurrentlyEmbedded(wallet)
        ) {
          this.provider.connect(
            {
              jsBridgeKey: wallet.jsBridgeKey,
            },
            { tonProof: token }
          );
        } else {
          const url = this.provider.connect(
            {
              universalLink: wallet.universalLink,
              bridgeUrl: wallet.bridgeUrl,
            },
            { tonProof: token }
          );
          window.open(url);
        }

        await new Promise((resolve, reject) => {
          this.unsubscribe = this.provider.onStatusChange(
            (wallet) => {
              if (wallet) {
                resolve(1);
              } else {
                reject(new Error('TON Wallet not detected'));
              }
            },
            (err) => {
              reject(err.message);
            }
          );
        });
      } catch (e: any) {
        throw new Error(e.message);
      }
    }

    const rawAddress = this.provider.wallet!.account.address; // like '0:abcdef123456789...'
    const bouncableUserFriendlyAddress = toUserFriendlyAddress(
      rawAddress || ''
    );

    return {
      account: bouncableUserFriendlyAddress,
      chain: {
        id: TON_CHAIN_ID,
        unsupported: false,
      },
      provider: this.provider,
    } as Required<ConnectorData>;
  }

  async disconnect(): Promise<void> {
    this.unsubscribe();
    await this.provider.disconnect();
  }

  async getAccount(): Promise<`0x${string}`> {
    const rawAddress = this.provider.wallet?.account.address; // like '0:abcdef123456789...'
    const bouncableUserFriendlyAddress = toUserFriendlyAddress(
      rawAddress || ''
    );
    return bouncableUserFriendlyAddress as any;
  }

  async getChainId(): Promise<number> {
    return TON_CHAIN_ID;
  }

  async getWalletClient(config?: { chainId?: number }): Promise<any> {
    const provider = this.provider;

    return {
      async signTypedData({ message }: any) {
        return {
          ...provider.account,
          ...provider.wallet!.connectItems!.tonProof,
        };
      },
    };
  }

  async isAuthorized(): Promise<boolean> {
    return this.provider.connected;
  }

  protected onAccountsChanged(accounts: `0x${string}`[]): void {
    throw new Error('Method onAccountsChanged not implemented.');
  }

  protected onChainChanged(chain: string | number): void {
    throw new Error('Method onChainChanged not implemented.');
  }

  protected async onDisconnect(error: Error) {
    this.emit('disconnect');
  }
}
